import { useState, FormEvent, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SEO from '../../components/SEO';

const FORM_URL = 'https://readdy.ai/api/form/d6m81f85dsf7sr1n682g';
const ZAPIER_WEBHOOK_URL = '';

const CITY_DATA: Record<string, {
  headline: string;
  subheadline: string;
  urgency: string;
  heroSeq: string;
  geo: string;
  nearbyAreas: string[];
  testimonial: { name: string; text: string; service: string; imgSeq: string };
}> = {
  toronto: {
    headline: 'Free Security Quote – Toronto',
    subheadline: 'Protecting Toronto homes & businesses since 1999. Get your custom security quote in minutes.',
    urgency: 'Toronto homeowners: Limited installation slots this week — book your free assessment now.',
    heroSeq: 'gads-toronto',
    geo: 'Toronto, ON',
    nearbyAreas: ['North York', 'Scarborough', 'Etobicoke', 'East York', 'Downtown Toronto'],
    testimonial: { name: 'Kevin M.', text: 'Alliance installed our alarm and cameras in one day. The team was professional and the system is flawless. Best security company in Toronto.', service: 'Alarm + Cameras', imgSeq: 'gads-t1' },
  },
  mississauga: {
    headline: 'Free Security Quote – Mississauga',
    subheadline: 'Mississauga\'s trusted security experts. Alarm systems, cameras & monitoring — custom-quoted for your property.',
    urgency: 'Mississauga residents: Get a free on-site security assessment this week.',
    heroSeq: 'gads-mississauga',
    geo: 'Mississauga, ON',
    nearbyAreas: ['Port Credit', 'Streetsville', 'Meadowvale', 'Erin Mills', 'Cooksville'],
    testimonial: { name: 'Priya S.', text: 'The quote was detailed and honest — no upselling. They installed exactly what I needed at a fair price. Highly recommend to all Mississauga homeowners.', service: 'Home Security', imgSeq: 'gads-m1' },
  },
  brampton: {
    headline: 'Free Security Quote – Brampton',
    subheadline: 'Brampton\'s #1 rated security company. Professional alarm systems, cameras & 24/7 monitoring.',
    urgency: 'Brampton special: Free professional security assessment with every quote this month.',
    heroSeq: 'gads-brampton',
    geo: 'Brampton, ON',
    nearbyAreas: ['Bramalea', 'Heart Lake', 'Springdale', 'Castlemore', 'Mount Pleasant'],
    testimonial: { name: 'David R.', text: 'Best security company in the GTA. Compared 4 quotes — Alliance was the most transparent and professional. Installed in Brampton within 48 hours.', service: 'Commercial Security', imgSeq: 'gads-b1' },
  },
  oakville: {
    headline: 'Free Security Quote – Oakville',
    subheadline: 'Premium security solutions for Oakville homes & businesses. Licensed, insured, and locally trusted.',
    urgency: 'Oakville homeowners: Protect your investment — get a free quote today.',
    heroSeq: 'gads-oakville',
    geo: 'Oakville, ON',
    nearbyAreas: ['Glen Abbey', 'Kerr Village', 'Bronte', 'River Oaks', 'Palermo'],
    testimonial: { name: 'Sandra L.', text: 'We have a large Oakville home and needed comprehensive coverage. Alliance designed a perfect system — cameras, alarm, and smart locks. Exceptional service.', service: 'Full Home Security', imgSeq: 'gads-o1' },
  },
  hamilton: {
    headline: 'Free Security Quote – Hamilton',
    subheadline: 'Hamilton\'s trusted security specialists. Alarm systems, HD cameras & 24/7 monitoring for homes and businesses.',
    urgency: 'Hamilton residents: Same-week installation available — request your free quote now.',
    heroSeq: 'gads-hamilton',
    geo: 'Hamilton, ON',
    nearbyAreas: ['Ancaster', 'Dundas', 'Stoney Creek', 'Waterdown', 'Binbrook'],
    testimonial: { name: 'Tom B.', text: 'Alliance secured our Hamilton warehouse with cameras and access control. The system has been running perfectly for 2 years. Great local company.', service: 'Commercial Security', imgSeq: 'gads-h1' },
  },
  vaughan: {
    headline: 'Free Security Quote – Vaughan',
    subheadline: 'Vaughan\'s top-rated security company. Smart alarm systems, cameras & access control for every property type.',
    urgency: 'Vaughan homeowners: Limited slots this week — get your free security assessment today.',
    heroSeq: 'gads-vaughan',
    geo: 'Vaughan, ON',
    nearbyAreas: ['Woodbridge', 'Maple', 'Kleinburg', 'Concord', 'Thornhill'],
    testimonial: { name: 'Marco V.', text: 'Had Alliance install a full security system in our Vaughan home. The technician was knowledgeable and the app control is fantastic. Highly recommend.', service: 'Smart Home Security', imgSeq: 'gads-v1' },
  },
  'richmond-hill': {
    headline: 'Free Security Quote – Richmond Hill',
    subheadline: 'Richmond Hill\'s trusted security experts. Professional installation, 24/7 monitoring & lifetime support.',
    urgency: 'Richmond Hill: Free professional security assessment — book your slot today.',
    heroSeq: 'gads-richmond-hill',
    geo: 'Richmond Hill, ON',
    nearbyAreas: ['Oak Ridges', 'Langstaff', 'Jefferson', 'Bayview Hill', 'Mill Pond'],
    testimonial: { name: 'Lisa C.', text: 'Alliance installed cameras and an alarm system in our Richmond Hill home. The process was seamless and the team was incredibly professional. 5 stars.', service: 'Alarm + Cameras', imgSeq: 'gads-rh1' },
  },
  whitby: {
    headline: 'Free Security Quote – Whitby',
    subheadline: 'Whitby\'s reliable security specialists. Alarm systems, cameras & monitoring — tailored to your property.',
    urgency: 'Whitby residents: Same-week installation available — get your free quote now.',
    heroSeq: 'gads-whitby',
    geo: 'Whitby, ON',
    nearbyAreas: ['Port Whitby', 'Brooklin', 'Pringle Creek', 'Rolling Acres', 'Blue Grass Meadows'],
    testimonial: { name: 'James W.', text: 'After a break-in on our street, we called Alliance. They came out the next day, assessed our Whitby home, and installed a complete system. Excellent service.', service: 'Complete Security Package', imgSeq: 'gads-w1' },
  },
  ajax: {
    headline: 'Free Security Quote – Ajax',
    subheadline: 'Ajax & Durham Region security experts. Professional alarm systems, cameras & 24/7 monitoring.',
    urgency: 'Ajax homeowners: Get a free security assessment — limited slots available this week.',
    heroSeq: 'gads-ajax',
    geo: 'Ajax, ON',
    nearbyAreas: ['Pickering', 'Whitby', 'Oshawa', 'Courtice', 'Bowmanville'],
    testimonial: { name: 'Rachel T.', text: 'Alliance secured our Ajax home with a wireless alarm and outdoor cameras. The installation was quick and the system works perfectly. Very happy with the service.', service: 'Home Security', imgSeq: 'gads-a1' },
  },
  oshawa: {
    headline: 'Free Security Quote – Oshawa',
    subheadline: 'Oshawa\'s trusted security company. Alarm systems, HD cameras & professional monitoring for homes and businesses.',
    urgency: 'Oshawa residents: Free security assessment available this week — book now.',
    heroSeq: 'gads-oshawa',
    geo: 'Oshawa, ON',
    nearbyAreas: ['Whitby', 'Ajax', 'Courtice', 'Bowmanville', 'Clarington'],
    testimonial: { name: 'Mike D.', text: 'Great experience with Alliance in Oshawa. They installed cameras and an alarm system for our business. Professional, fast, and fairly priced. Highly recommend.', service: 'Business Security', imgSeq: 'gads-os1' },
  },
};

const DEFAULT_CITY = 'toronto';

const SERVICES = [
  { value: 'Alarm System', icon: 'ri-alarm-warning-line' },
  { value: 'Security Cameras', icon: 'ri-camera-line' },
  { value: 'Access Control', icon: 'ri-door-lock-line' },
  { value: '24/7 Monitoring', icon: 'ri-eye-line' },
  { value: 'Smart Home', icon: 'ri-home-gear-line' },
  { value: 'Video Doorbell', icon: 'ri-door-open-line' },
];

const TRUST_BADGES = [
  { icon: 'ri-shield-check-line', label: 'Licensed & Insured' },
  { icon: 'ri-time-line', label: '2-Hour Response' },
  { icon: 'ri-star-fill', label: '4.9★ Google Rating' },
  { icon: 'ri-price-tag-3-line', label: 'No Hidden Fees' },
  { icon: 'ri-award-line', label: 'BBB A+ Rated' },
  { icon: 'ri-tools-line', label: '30,000+ Installs' },
];

const WHY_US = [
  { icon: 'ri-shield-star-line', title: 'Factory-Certified Technicians', desc: 'Certified by Honeywell, DSC, Hikvision, Qolsys & more.' },
  { icon: 'ri-time-line', title: 'Same-Day Service Available', desc: 'Emergency support 24/7 across the entire GTA.' },
  { icon: 'ri-money-dollar-circle-line', title: 'Transparent, Itemized Quotes', desc: 'No hidden fees. No pressure. Just honest pricing.' },
  { icon: 'ri-customer-service-2-line', title: 'Lifetime Support Included', desc: 'We support your system for its entire lifespan.' },
];

function useCountUp(target: number, duration: number, start: boolean) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

export default function CityQuotePage() {
  const [searchParams] = useSearchParams();
  const cityParam = (searchParams.get('city') || DEFAULT_CITY).toLowerCase().replace(/\s+/g, '-');
  const cityData = CITY_DATA[cityParam] || CITY_DATA[DEFAULT_CITY];
  const cityName = cityData.geo.split(',')[0];

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    propertyType: '',
    city: cityName,
    services: [] as string[],
    details: '',
  });
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [detailsLen, setDetailsLen] = useState(0);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);

  const installs = useCountUp(30000, 2000, statsVisible);
  const years = useCountUp(25, 1500, statsVisible);
  const satisfaction = useCountUp(98, 1800, statsVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  // Sync city field if param changes
  useEffect(() => {
    setFormData(prev => ({ ...prev, city: cityName }));
  }, [cityName]);

  const toggleService = (val: string) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(val)
        ? prev.services.filter(s => s !== val)
        : [...prev.services, val],
    }));
  };

  const isValid =
    formData.fullName.trim() !== '' &&
    formData.email.trim() !== '' &&
    formData.phone.trim() !== '' &&
    formData.propertyType !== '' &&
    formData.services.length > 0;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!isValid || formData.details.length > 500) return;
    setStatus('submitting');

    const params = new URLSearchParams();
    params.append('fullName', formData.fullName);
    params.append('email', formData.email);
    params.append('phone', formData.phone);
    params.append('propertyType', formData.propertyType);
    params.append('city', formData.city);
    params.append('services', formData.services.join(', '));
    params.append('details', formData.details);
    params.append('source', `Google Ads – ${cityName}`);

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        if (ZAPIER_WEBHOOK_URL) {
          try {
            await fetch(ZAPIER_WEBHOOK_URL, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                ...formData,
                services: formData.services.join(', '),
                source: `Google Ads – ${cityName}`,
                submitted_at: new Date().toISOString(),
              }),
            });
          } catch { /* non-blocking */ }
        }
        setStatus('success');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const faqs = [
    { q: `Do you service ${cityName}?`, a: `Yes! We serve all of ${cityName} and surrounding areas including ${cityData.nearbyAreas.join(', ')}. Our certified technicians are local and can typically schedule within 48 hours.` },
    { q: 'Is the quote really free with no obligation?', a: 'Absolutely. There is zero cost and zero pressure. We provide a detailed, itemized quote and you decide if you want to proceed.' },
    { q: 'How quickly will I receive my quote?', a: 'Our security specialists respond within 2 business hours. For urgent requests, call us directly at 1-888-458-9181.' },
    { q: 'What happens after I submit the form?', a: `A certified security specialist reviews your details, prepares a custom quote for your ${cityName} property, and contacts you by phone or email within 2 hours.` },
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <SEO
        title={`${cityData.headline} | Alliance Security Systems`}
        description={`Get a free, no-obligation security quote in ${cityName}. Alarm systems, security cameras, access control & 24/7 monitoring. Licensed experts. 2-hour response. Call 1-888-458-9181.`}
        keywords={`free security quote ${cityName}, alarm system ${cityName}, security cameras ${cityName}, home security ${cityName} Ontario`}
        canonical={`/quote?city=${cityParam}`}
      />

      {/* Urgency Bar */}
      <div className="bg-[#0A1628] text-white py-2 px-4 text-center text-xs font-medium">
        <span className="text-[#D4A853] font-bold">⚡ {cityData.urgency}</span>
        <a href="tel:18884589181" className="underline ml-2 hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">Call 1-888-458-9181</a>
      </div>

      {/* Navbar */}
      <nav className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="flex flex-col leading-none border-2 border-[#D4A853] rounded-lg px-3 py-2 bg-[#0A1628]">
              <span className="text-xl font-extrabold tracking-wider text-[#D4A853]">ALLIANCE</span>
              <span className="text-[9px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-6">
            <Link to="/alarm-packages" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Alarms</Link>
            <Link to="/camera-packages" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Cameras</Link>
            <Link to="/access-control" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Access Control</Link>
            <Link to="/live-monitoring" className="text-sm text-gray-600 hover:text-[#D4A853] transition-colors cursor-pointer">Monitoring</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 text-sm font-semibold text-[#0A1628] hover:text-[#D4A853] transition-colors cursor-pointer whitespace-nowrap">
              <div className="w-7 h-7 bg-[#D4A853] rounded-full flex items-center justify-center">
                <i className="ri-phone-fill text-white text-xs"></i>
              </div>
              1-888-458-9181
            </a>
          </div>
          <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden w-9 h-9 flex items-center justify-center text-gray-700 cursor-pointer">
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 px-4 pb-4 space-y-1">
            <Link to="/alarm-packages" className="block py-2.5 text-sm text-gray-700 border-b border-gray-50">Alarms</Link>
            <Link to="/camera-packages" className="block py-2.5 text-sm text-gray-700 border-b border-gray-50">Cameras</Link>
            <Link to="/access-control" className="block py-2.5 text-sm text-gray-700 border-b border-gray-50">Access Control</Link>
            <Link to="/live-monitoring" className="block py-2.5 text-sm text-gray-700 border-b border-gray-50">Monitoring</Link>
            <a href="tel:18884589181" className="flex items-center gap-2 py-2.5 text-sm font-semibold text-[#D4A853]"><i className="ri-phone-line"></i>1-888-458-9181</a>
          </div>
        )}
      </nav>

      <main>
        {/* ── HERO + FORM ── */}
        <section className="relative min-h-[94vh] flex items-stretch overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={`https://public.readdy.ai/ai/img_res/33f7bbbfd35a38e321072683d9b49652.jpg${cityData.heroSeq}&orientation=landscape`}
              alt={`Security systems in ${cityName}`}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A1628]/93 via-[#0A1628]/75 to-[#0A1628]/25"></div>
          </div>

          <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 py-14 flex flex-col lg:flex-row items-center gap-10 lg:gap-16">
            {/* Left — Headline */}
            <div className="flex-1 text-white">
              {/* City pill */}
              <div className="inline-flex items-center gap-2 bg-[#D4A853]/20 border border-[#D4A853]/50 text-[#D4A853] px-4 py-2 rounded-full text-xs font-bold tracking-widest mb-5 whitespace-nowrap">
                <i className="ri-map-pin-2-fill"></i>
                SERVING {cityName.toUpperCase()} & SURROUNDING AREAS
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-5">
                {cityData.headline.split('–')[0].trim()}<br />
                <span className="text-[#D4A853]">Free Security Quote</span><br />
                <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white/90">– {cityName}</span>
              </h1>

              <p className="text-lg text-white/80 mb-7 max-w-lg leading-relaxed">
                {cityData.subheadline}
              </p>

              {/* Trust badges */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-8">
                {TRUST_BADGES.map((b, i) => (
                  <div key={i} className="flex items-center gap-2.5 bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl px-3 py-2.5">
                    <div className="w-7 h-7 flex items-center justify-center shrink-0">
                      <i className={`${b.icon} text-[#D4A853] text-base`}></i>
                    </div>
                    <span className="text-xs font-semibold text-white/90 leading-tight">{b.label}</span>
                  </div>
                ))}
              </div>

              {/* City-specific testimonial */}
              <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-5 max-w-md">
                <div className="flex items-center gap-1 mb-2">
                  {[1,2,3,4,5].map(s => <i key={s} className="ri-star-fill text-[#D4A853] text-xs"></i>)}
                </div>
                <p className="text-sm text-white/85 italic mb-3">"{cityData.testimonial.text}"</p>
                <div className="flex items-center gap-2">
                  <img
                    src={`https://readdy.ai/api/search-image?query=professional%20person%20smiling%20headshot%20portrait%20clean%20white%20background%20natural%20lighting%20diverse&width=80&height=80&seq=${cityData.testimonial.imgSeq}&orientation=squarish`}
                    alt={cityData.testimonial.name}
                    className="w-9 h-9 rounded-full border-2 border-[#D4A853]/50 object-cover object-top"
                  />
                  <div>
                    <p className="text-xs font-bold text-white">{cityData.testimonial.name} · {cityName}</p>
                    <p className="text-[10px] text-white/60">{cityData.testimonial.service}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — Form */}
            <div className="w-full lg:w-[480px] shrink-0">
              {status === 'success' ? (
                <SuccessCard cityName={cityName} />
              ) : (
                <form
                  id="city-quote-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="bg-white rounded-3xl shadow-2xl overflow-hidden"
                >
                  <div className="bg-[#0A1628] px-7 py-5">
                    <h2 className="text-white font-bold text-xl">Get My Free {cityName} Quote</h2>
                    <p className="text-white/60 text-xs mt-1">We respond within 2 hours · No spam, ever</p>
                  </div>

                  <div className="px-7 py-6 space-y-5">
                    {/* Name + Phone */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Full Name *</label>
                        <input
                          type="text"
                          name="fullName"
                          required
                          value={formData.fullName}
                          onChange={e => setFormData(p => ({ ...p, fullName: e.target.value }))}
                          placeholder="John Smith"
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Phone *</label>
                        <input
                          type="tel"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))}
                          placeholder="(416) 555-0123"
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent"
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                        placeholder="john@example.com"
                        className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent"
                      />
                    </div>

                    {/* Property + City (pre-filled) */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Property Type *</label>
                        <select
                          name="propertyType"
                          required
                          value={formData.propertyType}
                          onChange={e => setFormData(p => ({ ...p, propertyType: e.target.value }))}
                          className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent cursor-pointer bg-white"
                        >
                          <option value="">Select...</option>
                          <option value="Detached Home">Detached Home</option>
                          <option value="Condo / Apartment">Condo / Apt</option>
                          <option value="Townhouse">Townhouse</option>
                          <option value="Small Business">Small Business</option>
                          <option value="Office / Commercial">Office / Commercial</option>
                          <option value="Warehouse / Industrial">Warehouse</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-bold text-gray-700 mb-1.5">Your City</label>
                        <div className="w-full px-3.5 py-2.5 border border-[#D4A853]/50 bg-[#D4A853]/5 rounded-xl text-sm font-semibold text-[#0A1628] flex items-center gap-2">
                          <i className="ri-map-pin-2-fill text-[#D4A853] text-base"></i>
                          {cityName}
                        </div>
                        <input type="hidden" name="city" value={formData.city} />
                      </div>
                    </div>

                    {/* Services */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-2">Services Needed * <span className="text-gray-400 font-normal">(select all that apply)</span></label>
                      <div className="grid grid-cols-3 gap-2">
                        {SERVICES.map(svc => (
                          <button
                            key={svc.value}
                            type="button"
                            onClick={() => toggleService(svc.value)}
                            className={`flex flex-col items-center gap-1.5 py-2.5 px-2 rounded-xl border-2 text-center transition-all cursor-pointer ${
                              formData.services.includes(svc.value)
                                ? 'border-[#D4A853] bg-[#D4A853]/8 text-[#D4A853]'
                                : 'border-gray-200 text-gray-500 hover:border-[#D4A853]/40'
                            }`}
                          >
                            <div className="w-5 h-5 flex items-center justify-center">
                              <i className={`${svc.icon} text-base`}></i>
                            </div>
                            <span className="text-[10px] font-semibold leading-tight">{svc.value}</span>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Details */}
                    <div>
                      <label className="block text-xs font-bold text-gray-700 mb-1.5">Additional Details <span className="text-gray-400 font-normal">(optional)</span></label>
                      <textarea
                        name="details"
                        rows={2}
                        maxLength={500}
                        value={formData.details}
                        onChange={e => { setFormData(p => ({ ...p, details: e.target.value })); setDetailsLen(e.target.value.length); }}
                        placeholder={`e.g. 3-bedroom home in ${cityName}, need cameras at back...`}
                        className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#D4A853] focus:border-transparent resize-none"
                      />
                      <p className="text-[10px] text-gray-400 text-right mt-0.5">{detailsLen}/500</p>
                    </div>

                    {status === 'error' && (
                      <div className="flex items-center gap-2 bg-red-50 text-red-600 px-4 py-3 rounded-xl text-xs">
                        <i className="ri-error-warning-line text-base"></i>
                        Something went wrong. Please try again or call 1-888-458-9181.
                      </div>
                    )}

                    <button
                      type="submit"
                      disabled={!isValid || status === 'submitting'}
                      className="w-full bg-[#D4A853] text-white py-4 rounded-2xl font-bold text-base hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-[#D4A853]/25 flex items-center justify-center gap-2"
                    >
                      {status === 'submitting' ? (
                        <><i className="ri-loader-4-line animate-spin text-lg"></i>Sending...</>
                      ) : (
                        <><i className="ri-send-plane-fill text-lg"></i>Get My Free {cityName} Quote</>
                      )}
                    </button>

                    <p className="text-center text-[10px] text-gray-400 leading-relaxed">
                      <i className="ri-lock-2-line mr-1"></i>
                      Your information is 100% private and never shared.
                    </p>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* ── STATS BAR ── */}
        <section ref={statsRef} className="bg-[#D4A853] py-10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-3 gap-6 text-center">
              {[
                { value: installs, suffix: '+', label: 'GTA Installations' },
                { value: years, suffix: ' yrs', label: 'Years of Experience' },
                { value: satisfaction, suffix: '%', label: 'Customer Satisfaction' },
              ].map((s, i) => (
                <div key={i}>
                  <p className="text-4xl sm:text-5xl font-extrabold text-white">{s.value.toLocaleString()}{s.suffix}</p>
                  <p className="text-white/80 text-sm mt-1 font-medium">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── WHY ALLIANCE ── */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">WHY CHOOSE US IN {cityName.toUpperCase()}</span>
                <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A1628] mb-6">{cityName}'s Most Trusted Security Company Since 1999</h2>
                <p className="text-gray-600 text-base leading-relaxed mb-8">
                  We're a local, family-owned business with certified technicians who live and work in {cityName} and the surrounding communities. Every quote is prepared by a real expert — not an algorithm.
                </p>
                <div className="space-y-4">
                  {WHY_US.map((item, i) => (
                    <div key={i} className="flex items-start gap-4">
                      <div className="w-11 h-11 bg-[#D4A853]/10 rounded-xl flex items-center justify-center shrink-0">
                        <i className={`${item.icon} text-xl text-[#D4A853]`}></i>
                      </div>
                      <div>
                        <p className="font-bold text-[#0A1628] text-sm">{item.title}</p>
                        <p className="text-gray-500 text-sm">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="relative">
                <div className="w-full h-[420px] rounded-3xl overflow-hidden shadow-2xl">
                  <img
                    src="https://readdy.ai/api/search-image?query=professional%20security%20technician%20in%20uniform%20installing%20security%20camera%20on%20wall%20of%20modern%20home%20smiling%20confident%20expert%20installation%20residential%20property%20daylight%20clear%20sky%20Ontario%20Canada&width=800&height=900&seq=gads-tech-why&orientation=portrait"
                    alt="Alliance Security technician"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="absolute -bottom-5 -left-5 bg-white rounded-2xl shadow-xl p-4 border border-gray-100 flex items-center gap-3">
                  <div className="w-12 h-12 bg-[#003DA5] rounded-xl flex items-center justify-center shrink-0">
                    <span className="text-white font-extrabold text-lg leading-none">A+</span>
                  </div>
                  <div>
                    <p className="text-xs font-bold text-[#0A1628]">BBB Accredited</p>
                    <p className="text-[10px] text-gray-500">A+ Rating Since 1999</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── SERVICE AREAS ── */}
        <section className="py-12 bg-[#F8F9FA]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
            <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">AREAS WE SERVE IN {cityName.toUpperCase()}</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-[#0A1628] mb-6">We Cover All of {cityName} & Nearby</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {[cityName, ...cityData.nearbyAreas].map((area, i) => (
                <span
                  key={i}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border ${i === 0 ? 'bg-[#D4A853] text-white border-[#D4A853]' : 'bg-white text-gray-700 border-gray-200 hover:border-[#D4A853]/50 transition-colors'}`}
                >
                  <i className="ri-map-pin-2-fill mr-1.5 text-xs"></i>{area}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ── HOW IT WORKS ── */}
        <section className="py-16 sm:py-20 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 text-center">
            <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">HOW IT WORKS</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-[#0A1628] mb-12">Your {cityName} Quote in 3 Simple Steps</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 relative">
              <div className="hidden sm:block absolute top-10 left-[calc(16.67%+1rem)] right-[calc(16.67%+1rem)] h-0.5 bg-[#D4A853]/30"></div>
              {[
                { step: 1, icon: 'ri-file-list-3-line', title: 'Fill the Form', desc: 'Tell us about your property in ' + cityName + '. Takes under 2 minutes.' },
                { step: 2, icon: 'ri-customer-service-2-line', title: 'Expert Review', desc: 'A certified specialist prepares a custom quote for your property.' },
                { step: 3, icon: 'ri-shield-check-line', title: 'Get Protected', desc: 'Receive your detailed quote within 2 hours and schedule installation.' },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center relative">
                  <div className="w-20 h-20 bg-[#0A1628] rounded-2xl flex items-center justify-center mb-5 relative z-10 shadow-xl">
                    <i className={`${item.icon} text-3xl text-[#D4A853]`}></i>
                    <div className="absolute -top-2 -right-2 w-7 h-7 bg-[#D4A853] rounded-full flex items-center justify-center text-white text-xs font-extrabold">{item.step}</div>
                  </div>
                  <h3 className="text-lg font-bold text-[#0A1628] mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-500 leading-relaxed max-w-xs">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── FAQ ── */}
        <section className="py-16 sm:py-20 bg-[#F8F9FA]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-10">
              <span className="inline-block bg-[#D4A853]/10 text-[#D4A853] px-5 py-2 rounded-full text-xs font-bold tracking-widest mb-4 whitespace-nowrap">FAQ</span>
              <h2 className="text-3xl font-extrabold text-[#0A1628]">Common Questions from {cityName} Residents</h2>
            </div>
            <div className="space-y-3">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-semibold text-[#0A1628] text-sm pr-4">{faq.q}</span>
                    <div className="w-7 h-7 flex items-center justify-center shrink-0">
                      <i className={`ri-${openFaq === i ? 'subtract' : 'add'}-line text-[#D4A853] text-lg`}></i>
                    </div>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-sm text-gray-600 leading-relaxed">{faq.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── BOTTOM CTA ── */}
        <section className="py-16 bg-[#0A1628] relative overflow-hidden">
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }}></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="w-16 h-16 bg-[#D4A853] rounded-2xl flex items-center justify-center mx-auto mb-6">
              <i className="ri-shield-check-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Ready to Protect Your {cityName} Property?</h2>
            <p className="text-white/70 text-base mb-8 max-w-xl mx-auto">
              Join 30,000+ GTA homeowners and businesses who trust Alliance Security. Get your free {cityName} quote today — no commitment required.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#city-quote-form"
                onClick={e => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="inline-flex items-center gap-2 bg-[#D4A853] text-white px-8 py-4 rounded-full font-bold text-base hover:bg-[#B8923D] transition-all cursor-pointer whitespace-nowrap shadow-xl"
              >
                <i className="ri-file-list-3-line text-lg"></i>
                Get My Free {cityName} Quote
              </a>
              <a
                href="tel:18884589181"
                className="inline-flex items-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-base hover:border-[#D4A853] hover:text-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-line text-lg"></i>
                1-888-458-9181
              </a>
            </div>
            <p className="text-white/40 text-xs mt-6">Mon–Fri 8am–8pm · Sat 9am–5pm · 24/7 Emergency Line</p>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-[#050B14] text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <Link to="/" className="flex flex-col leading-none">
            <span className="text-lg font-extrabold tracking-wider text-[#D4A853]">ALLIANCE</span>
            <span className="text-[9px] font-semibold tracking-[0.25em] text-[#D4A853]">SECURITY SYSTEMS</span>
          </Link>
          <div className="flex items-center gap-6 text-xs text-white/50">
            <Link to="/privacy-policy" className="hover:text-white/80 cursor-pointer">Privacy Policy</Link>
            <Link to="/contact" className="hover:text-white/80 cursor-pointer">Contact</Link>
            <Link to="/faq" className="hover:text-white/80 cursor-pointer">FAQ</Link>
            <Link to="/get-quote" className="hover:text-white/80 cursor-pointer">Get a Quote</Link>
          </div>
          <p className="text-white/40 text-xs">© 2025 Alliance Security Systems. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

function SuccessCard({ cityName }: { cityName: string }) {
  return (
    <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
      <div className="bg-emerald-500 px-7 py-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
            <i className="ri-check-double-line text-white text-xl"></i>
          </div>
          <div>
            <h2 className="text-white font-bold text-lg">Quote Request Sent!</h2>
            <p className="text-white/80 text-xs">We'll be in touch within 2 hours</p>
          </div>
        </div>
      </div>
      <div className="px-7 py-8 text-center">
        <div className="w-20 h-20 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-5">
          <i className="ri-shield-check-line text-4xl text-emerald-500"></i>
        </div>
        <h3 className="text-xl font-bold text-[#0A1628] mb-3">Thank You!</h3>
        <p className="text-gray-500 text-sm leading-relaxed mb-6">
          A certified {cityName} security specialist is reviewing your details and will contact you with a personalized quote within <strong>2 business hours</strong>.
        </p>
        <div className="bg-[#F8F9FA] rounded-2xl p-4 mb-6 text-left space-y-2">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">What happens next</p>
          {[
            'Specialist reviews your property details',
            'Custom quote prepared for your ' + cityName + ' property',
            'You receive a call or email within 2 hours',
          ].map((step, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-gray-600">
              <div className="w-5 h-5 bg-[#D4A853] rounded-full flex items-center justify-center shrink-0">
                <span className="text-white text-[10px] font-bold">{i + 1}</span>
              </div>
              {step}
            </div>
          ))}
        </div>
        <a
          href="tel:18884589181"
          className="flex items-center justify-center gap-2 bg-[#0A1628] text-white px-6 py-3 rounded-full text-sm font-semibold hover:bg-[#D4A853] transition-all cursor-pointer whitespace-nowrap"
        >
          <i className="ri-phone-line"></i>
          Call Us: 1-888-458-9181
        </a>
      </div>
    </div>
  );
}
